import type { InboundCallLeg } from '../../types/speedToLead.js';

// NCCO BUILDERS for inbound call forwarding. PURE: the answer webhook builds these from stored state and
// awaits NOTHING (no send, no model), so it answers well inside a second.
//
// ⚠️⚠️ NO VOICEMAIL, EVER. These builders emit `talk`, `connect` (with an `onAnswer` whisper), the DTMF `input`, and,
// from Stage 3B, ONE `record` action ahead of the connect when the club records calls (`buildInboundRecordAction`,
// driven by `recordingPlan`, so recording off removes it). No recording NOTICE is played (Scott, 2026-10-22).
// ⚠️ A recording of a call nobody accepted is DISCARDED, never stored (callMedia.ts). A source guard scans this file
// for voicemail wording and the tests scan every NCCO produced.
// ⚠️ No outbound calls except the `connect` leg forwarding a call the person themselves placed.

export type Ncco = Array<Record<string, unknown>>;

const digits = (v: string) => v.replace(/[^0-9]/g, '');

/** Short fixed TTS, Australian English. The caller hears it and the call ends. */
export function buildFixedMessageNcco(text: string): Ncco {
  return [{ action: 'talk', text, language: 'en-AU', bargeIn: false }];
}

/**
 * ⚠️⚠️ THE ACCEPT PROMPT, ALWAYS APPENDED, NEVER EDITABLE. A carrier's voicemail "answers" the forwarded leg,
 * and Vonage reports that as an answered call: the whisper played into the mailbox and the caller was left
 * connected to a silently recording box (live, 2026-10-22). A person must press a key to take the call, so a
 * machine can never accept it. Shared by Stage 3's staff leg.
 */
export const ACCEPT_PROMPT = 'Press any key to take the call.';
/**
 * The acceptance window: the whisper + prompt plays TWICE back to back (two talk actions, no gap), then ONE
 * wait of ACCEPT_INPUT_TIMEOUT_SECONDS for a key. A key at any point, during either talk or the wait, accepts;
 * the single timeout is the only decline.
 */
export const ACCEPT_INPUT_TIMEOUT_SECONDS = 10;

/** The whisper text as spoken: the (editable) message, then the fixed prompt. */
export function spokenWhisper(message: string): string {
  const m = message.trim();
  return m ? `${m} ${ACCEPT_PROMPT}` : ACCEPT_PROMPT;
}

/**
 * The accept NCCO played to the answering leg only: the whisper plus the key prompt (barge-in, so a key
 * during the whisper counts), then ONE digit of DTMF reported to `acceptUrl`. The decision is made there.
 */
export function buildAcceptWhisperNcco(input: { text: string; acceptUrl: string }): Ncco {
  return [
    // Played twice, back to back: bargeIn on BOTH so a key during either one accepts at once.
    { action: 'talk', text: spokenWhisper(input.text), language: 'en-AU', bargeIn: true },
    { action: 'talk', text: spokenWhisper(input.text), language: 'en-AU', bargeIn: true },
    {
      action: 'input',
      type: ['dtmf'],
      dtmf: { maxDigits: 1, timeOut: ACCEPT_INPUT_TIMEOUT_SECONDS, submitOnHash: false },
      eventUrl: [input.acceptUrl],
    },
  ];
}

/** Played to the callee once they have accepted, while the legs are joined. */
export function buildAcceptedNcco(): Ncco {
  return [{ action: 'talk', text: 'Connecting you now.', language: 'en-AU', bargeIn: false }];
}

/** What a DTMF `input` result means. ⚠️ Anything but a real key press is NOT an acceptance. */
export function parseAcceptInput(body: unknown): { accepted: boolean } {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const raw = b.dtmf;
  const d = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  const digits = typeof raw === 'string' ? raw : d && typeof d.digits === 'string' ? d.digits : '';
  const timedOut = d ? d.timed_out === true : false;
  return { accepted: digits.trim() !== '' && !timedOut };
}

export interface LegNccoInput {
  leg: InboundCallLeg;
  /** The caller ID on the forwarded leg: a Vonage number by the documented rule. E.164 or digits. */
  from: string;
  ringSeconds: number;
  /** Synchronous: the next NCCO (next number, or the fixed message) comes back from here. */
  eventUrl: string;
  /** Serves the whisper NCCO to the callee when they answer. */
  whisperUrl: string;
  /** Our own ringback audio (the AU ring), played to the caller while the club's phone rings. */
  ringbackUrl: string;
}

export function buildLegNcco(input: LegNccoInput): Ncco {
  return [
    {
      action: 'connect',
      from: digits(input.from),
      timeout: input.ringSeconds,
      ringbackTone: input.ringbackUrl,
      // Second check: a detected answering machine hangs the leg up (treated as not answered). The key press
      // is the real control; this only saves the callee-less caller a wait. UNVERIFIED live on `connect`.
      machineDetection: 'hangup',
      eventType: 'synchronous',
      eventUrl: [input.eventUrl],
      endpoint: [{ type: 'phone', number: digits(input.leg.number), onAnswer: { url: input.whisperUrl } }],
    },
  ];
}

/**
 * What to return when leg `index` was not answered: the next leg, or the fixed message when none is left.
 * ⚠️ A pure function of the stored plan and the index, so a retried event gets the identical answer.
 */
export function nextNccoAfterLeg(input: {
  legs: InboundCallLeg[];
  index: number;
  fixedMessage: string;
  build: (leg: InboundCallLeg, index: number) => Ncco;
}): { ncco: Ncco; nextIndex: number | null } {
  const nextIndex = input.index + 1;
  const leg = input.legs[nextIndex];
  if (!leg) return { ncco: buildFixedMessageNcco(input.fixedMessage), nextIndex: null };
  return { ncco: input.build(leg, nextIndex), nextIndex };
}

/** The only action names these builders may ever produce. */
export const ALLOWED_NCCO_ACTIONS: readonly string[] = ['talk', 'connect', 'input', 'record'];

/**
 * The inbound call's recording, placed BEFORE the `connect` in the caller's first NCCO (Vonage's documented pattern
 * for recording a forwarded call). ⚠️ UNVERIFIED LIVE: that the club leg's whisper and key prompt are absent from it;
 * the live test listens for exactly that. Vonage posts the recording (and transcript) to the signed `/inbound-*` URLs.
 */
export function buildInboundRecordAction(input: { recordingEventUrl: string; transcriptionEventUrl: string; transcribe: boolean }): Ncco[number] {
  return {
    action: 'record',
    split: 'conversation',
    channels: 2,
    format: 'mp3',
    eventUrl: [input.recordingEventUrl],
    eventMethod: 'POST',
    ...(input.transcribe ? { transcription: { language: 'en-AU', eventUrl: [input.transcriptionEventUrl], eventMethod: 'POST' } } : {}),
  };
}
